import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../services/api';
import LazyImage from '../components/LazyImage';
import { RotatingLines } from 'react-loader-spinner';
import { getImageUrl } from '../utils/imageUtils';

const parseDescription = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return [{ type: 'text', content: raw }];
};

const DescriptionRenderer = ({ description }) => {
  const blocks = parseDescription(description);
  if (!blocks.length) return null;
  return (
    <div>
      {blocks.map((block, i) => {
        if (block.type === 'heading') return <p key={i} style={{fontWeight:'bold', textDecoration:'underline', margin:'0.4rem 0'}}>{block.content}</p>;
        if (block.type === 'subheading') return <p key={i} style={{fontWeight:'bold', margin:'0.4rem 0'}}>{block.content}</p>;
        return <p key={i} style={{margin:'0.4rem 0'}}>{block.content}</p>;
      })}
    </div>
  );
};

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.large};
  margin: 0 auto;
  padding: 2rem;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 1rem;
`;

const InfoSection = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 4rem;
  align-items: center;
  background: #f9f9f9;
  padding: 2rem;
  border-radius: 8px;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const PostCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const ImageContainer = styled.div`
  height: 200px;
  background: #f0f0f0;
`;

const Content = styled.div`
  padding: 1.5rem;
`;

const Workshops = () => {
  const [header, setHeader] = useState(null);
  const [info, setInfo] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/workshops/header'),
      api.get('/workshops/info'),
      api.get('/workshops/posts')
    ])
      .then(([h, i, p]) => {
        setHeader(h);
        setInfo(i);
        setPosts(p);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{display:'flex', justifyContent:'center', marginTop:'50px', minHeight:'100vh'}}>
      <RotatingLines strokeColor="grey" width="50" />
    </div>
  );

  return (
    <Container>
      {header && (
        <HeaderSection>
          <Title>{header.title}</Title>
          <p>{header.description}</p>
           {header.image && (
                <div style={{height: '300px', marginTop: '2rem', borderRadius:'8px', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:'#f0f0f0'}}>
                    <img src={getImageUrl(header.image)} alt={header.title} style={{width:'100%', height:'100%', objectFit:'contain'}} />
                </div>
            )}
        </HeaderSection>
      )}

      {info && (
        <InfoSection>
           <div>
              {info.title && <h2 style={{marginBottom:'1rem'}}>{info.title}</h2>}
              <p style={{whiteSpace:'pre-wrap'}}>{info.description}</p>
           </div>
           {info.image && (
             <div style={{height: '250px', borderRadius: '8px', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:'#f0f0f0'}}>
                <LazyImage src={info.image} alt={info.title} objectFit="contain" />
             </div>
           )}
        </InfoSection>
      )}

      <h2 style={{marginBottom: '2rem'}}>Workshops</h2>
      <Grid>
        {posts.map(post => (
          <PostCard key={post._id}>
            {post.images && post.images.length > 0 && (
              <ImageContainer>
                <LazyImage src={getImageUrl(post.images[0])} alt={post.title} objectFit="contain" />
              </ImageContainer>
            )}
            <Content>
              <h3>{post.title}</h3>
              {post.description && <DescriptionRenderer description={post.description} />}
            </Content>
          </PostCard>
        ))}
      </Grid>
      {posts.length === 0 && <p>No workshops scheduled currently.</p>}
    </Container>
  );
};

export default Workshops;
